import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Tag, Space, Typography, Modal, Form, Input, Select, message, Row, Col, Tooltip, InputNumber, Switch, Alert, Divider, Badge, Progress } from 'antd';
import useBreakpoint from '../../hooks/useBreakpoint';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DesktopOutlined,
  EyeOutlined,
  ReloadOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  SignalFilled,
  UploadOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  DiffOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../utils/rolePermissions';
import axios from 'axios';
import websocketService from '../../services/websocket';
import { formatTimestamp, getRelativeTime, isRecent, getCurrentUTCTimestamp, safeFormatTimestamp, safeGetRelativeTime } from '../../utils/timezone';
import logger from '../../services/logger';

const { Title, Text } = Typography;
const { Option } = Select;

const Devices = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const breakpoint = useBreakpoint();
  const [devices, setDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [stats, setStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
    error: 0,
    maintenance: 0,
    offline_data_pending: 0
  });
  const [searchText, setSearchText] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(true);

  // New states for MQTT actions
  const [controlModalVisible, setControlModalVisible] = useState(false);
  const [otaModalVisible, setOtaModalVisible] = useState(false);
  const [controlForm] = Form.useForm();
  const [otaForm] = Form.useForm();
  const [sendingControl, setSendingControl] = useState(false);
  const [sendingOta, setSendingOta] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [firmwares, setFirmwares] = useState([]);
  const [firmwareLoading, setFirmwareLoading] = useState(false);
  const [selectedFirmware, setSelectedFirmware] = useState(null);
  const [firmwareComparison, setFirmwareComparison] = useState(null);
  const [otaProgress, setOtaProgress] = useState({});

  useEffect(() => {
    fetchDevices();
    fetchCustomers();

    // Only fetch firmwares if user has permission
    if (hasPermission(user?.role, 'firmware.view_firmware')) {
      fetchFirmwares();
    }
    
    // WebSocket bağlantısı varsa polling yapma, yoksa fallback olarak polling yap
    const interval = setInterval(() => {
      // WebSocket bağlantısı kontrolü
      if (!websocketService.isConnected) {
        fetchDevices();
      }
    }, 10000); // 10 saniyeye çıkar, sadece fallback için
    
    // WebSocket ile cihaz durumu güncellemelerini dinle
    const token = localStorage.getItem('token');
    logger.debug('🔍 Devices.js - Token kontrolü:', token ? 'Token var' : 'Token yok');
    logger.debug('🔍 Devices.js - WebSocketService:', websocketService ? 'Mevcut' : 'Yok');
    
    if (token && websocketService) {
      try {
        logger.debug('🔍 Devices.js - WebSocket bağlantısı başlatılıyor...');
        websocketService.connect(token);
        
        // Telemetri güncellemelerini dinle
        const unsubscribeTelemetry = websocketService.subscribe('telemetry', (data) => {
          logger.debug('🔍 Devices.js - Telemetri güncellemesi alındı:', data);

          if (data.type === 'telemetry') {
            logger.debug(`🔍 Devices.js - Cihaz ${data.device_id} telemetri güncelleniyor`);

            // Cihaz listesini güncelle (last_seen, status ve cihaz bilgileri)
            setDevices(prevDevices => {
              const updatedDevices = prevDevices.map(device => {
                if (device.id === data.device_id) {
                  const oldStatus = device.status;
                  const newStatus = data.device_status || device.status; // Telemetri mesajında status varsa güncelle

                  if (oldStatus !== newStatus) {
                    logger.info(`✅ Devices.js - Cihaz ${device.name} durumu güncellendi: ${oldStatus} → ${newStatus}`);
                  }
                  logger.info(`✅ Devices.js - Cihaz ${device.name} last_seen güncellendi`);

                  return {
                    ...device,
                    status: newStatus, // Status bilgisini güncelle
                    last_seen: data.last_seen || getCurrentUTCTimestamp(),
                    // Operational status güncelle (telemetry'den)
                    operational_status: data.operational_status || device.operational_status,
                    status_summary: data.status_summary || device.status_summary,
                    // Cihaz bilgilerini güncelle (eğer varsa)
                    ...(data.device_info && {
                      serial_number: data.device_info.serial_number || device.serial_number,
                      model: data.device_info.model || device.model,
                      firmware_version: data.device_info.firmware_version || device.firmware_version,
                      stm32_firmware_version: data.device_info.stm32_firmware_version ?? device.stm32_firmware_version
                    })
                  };
                }
                return device;
              });

              // İstatistikleri güncelle (status değiştiği için)
              const deviceStats = {
                total: updatedDevices.length,
                online: updatedDevices.filter(d => d.status === 'online').length,
                offline: updatedDevices.filter(d => d.status === 'offline').length,
                error: updatedDevices.filter(d => d.status === 'error').length,
                maintenance: updatedDevices.filter(d => d.status === 'maintenance').length,
                offline_data_pending: updatedDevices.filter(d => d.status === 'offline_data_pending').length
              };
              setStats(deviceStats);
              logger.info('✅ Devices.js - Telemetri sonrası istatistikler güncellendi:', deviceStats);

              return updatedDevices;
            });
          }
        });

        // Cihaz durumu güncellemelerini dinle
        const unsubscribeDeviceStatus = websocketService.subscribe('device_status', (data) => {
          logger.debug('🔍 Devices.js - Device status güncellemesi alındı:', data);
          
          if (data.type === 'device_status') {
            logger.debug(`🔍 Devices.js - Cihaz ${data.device_id} durumu güncelleniyor: ${data.status}`);
            
            // Cihaz listesini güncelle
            setDevices(prevDevices => {
              const updatedDevices = prevDevices.map(device => {
                if (device.id === data.device_id) {
                  logger.info(`✅ Devices.js - Cihaz ${device.name} durumu güncellendi: ${device.status} → ${data.status}`);
                  return {
                    ...device,
                    status: data.status,
                    last_seen: data.last_seen || data.data?.timestamp || getCurrentUTCTimestamp(),
                    // Operational status güncelle (device_status event'inden)
                    ...(data.data?.operational_status && {
                      operational_status: data.data.operational_status
                    })
                  };
                }
                return device;
              });
              
              // İstatistikleri güncelle
              const deviceStats = {
                total: updatedDevices.length,
                online: updatedDevices.filter(d => d.status === 'online').length,
                offline: updatedDevices.filter(d => d.status === 'offline').length,
                error: updatedDevices.filter(d => d.status === 'error').length,
                maintenance: updatedDevices.filter(d => d.status === 'maintenance').length,
                offline_data_pending: updatedDevices.filter(d => d.status === 'offline_data_pending').length
              };
              setStats(deviceStats);
              logger.info('✅ Devices.js - İstatistikler güncellendi:', deviceStats);
              
              return updatedDevices;
            });
          }
        });

        // Cihaz bilgisi güncellemelerini dinle
        const unsubscribeDeviceInfo = websocketService.subscribe('device_info_update', (data) => {
          logger.debug('🔍 Devices.js - Device info güncellemesi alındı:', data);
          
          if (data.type === 'device_info_update') {
            logger.debug(`🔍 Devices.js - Cihaz ${data.device_id} bilgileri güncelleniyor:`, data.updated_fields);
            
            // Cihaz listesini güncelle
            setDevices(prevDevices => {
              const updatedDevices = prevDevices.map(device => {
                if (device.id === data.device_id) {
                  logger.info(`✅ Devices.js - Cihaz ${device.name} bilgileri güncellendi:`, data.updated_fields);
                  return {
                    ...device,
                    ...data.device_info,
                    last_seen: data.device_info?.last_seen || device.last_seen
                  };
                }
                return device;
              });
              
              return updatedDevices;
            });
          }
        });

        // OTA Progress güncellemelerini dinle (Real-time progress tracking)
        const unsubscribeOtaProgress = websocketService.subscribe('ota_progress', (data) => {
          logger.debug('🔍 Devices.js - OTA progress güncellemesi alındı:', data);

          if (data.type === 'ota_progress') {
            logger.info(`📊 Devices.js - Cihaz ${data.device_id} OTA progress: ${data.data.progress_percent}%, stage: ${data.data.stage}`);

            // OTA progress state'ini güncelle
            setOtaProgress(prev => ({
              ...prev,
              [data.device_id]: {
                percent: data.data.progress_percent,
                stage: data.data.stage,
                speed: data.data.download_speed_kbps,
                bytes: data.data.bytes_downloaded,
                total: data.data.total_bytes
              }
            }));
          }
        });

        // OTA Status güncellemelerini dinle (completed/failed için cleanup)
        const unsubscribeOtaStatus = websocketService.subscribe('ota_status', (data) => {
          logger.debug('🔍 Devices.js - OTA status güncellemesi alındı:', data);

          if (data.type === 'ota_status') {
            const statusData = data.data;

            // OTA tamamlandı veya başarısız oldu - 3 saniye sonra progress temizle
            if (statusData.status === 'completed' || statusData.status === 'failed') {
              logger.info(`✅ Devices.js - OTA ${statusData.status}: device_id=${data.device_id}`);

              setTimeout(() => {
                setOtaProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[data.device_id];
                  logger.info(`🧹 Devices.js - Progress temizlendi: device_id=${data.device_id}`);
                  return newProgress;
                });
              }, 3000);
            }
          }
        });

        logger.info('✅ Devices.js - WebSocket device_status listener eklendi');
        logger.info('✅ Devices.js - OTA Progress listeners eklendi');

        return () => {
          logger.debug('🔍 Devices.js - Cleanup çalıştırılıyor');
          clearInterval(interval);
          unsubscribeTelemetry();
          unsubscribeDeviceStatus();
          unsubscribeDeviceInfo();
          unsubscribeOtaProgress();
          unsubscribeOtaStatus();
        };
      } catch (error) {
        logger.error('❌ Devices.js - WebSocket bağlantı hatası:', error);
        return () => clearInterval(interval);
      }
    } else {
      logger.warn('⚠️ Devices.js - WebSocket bağlantısı kurulamadı (token veya service yok)');
    }
    
    return () => clearInterval(interval);
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get('/api/v1/devices');
      
      const devicesData = Array.isArray(response.data) ? response.data : [];
      setDevices(devicesData);

      // Calculate statistics
      const deviceStats = {
        total: devicesData.length,
        online: devicesData.filter(d => d.status === 'online').length,
        offline: devicesData.filter(d => d.status === 'offline').length,
        error: devicesData.filter(d => d.status === 'error').length,
        maintenance: devicesData.filter(d => d.status === 'maintenance').length,
        offline_data_pending: devicesData.filter(d => d.status === 'offline_data_pending').length
      };
      setStats(deviceStats);
    } catch (error) {
      logger.error('❌ Cihazlar yüklenirken hata:', error);
      logger.error('❌ Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || 'Cihazlar yüklenirken hata oluştu';
      message.error(errorMessage);
      setDevices([]); // Hata durumunda boş array set et
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/v1/customers');
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Customers fetch error:', error);
      setCustomers([]);
    }
  };

  const fetchFirmwares = async () => {
    try {
      setFirmwareLoading(true);
      const response = await axios.get('/api/v1/firmware');
      setFirmwares(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Firmwares fetch error:', error);
      message.error('Firmware listesi yüklenirken hata oluştu');
      setFirmwares([]);
    } finally {
      setFirmwareLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDevice(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    try {
      // Detayları backend'den çek (aktivasyon bilgilerini de içerir)
      const res = await axios.get(`/api/v1/devices/${record.id}`);
      const data = res.data || record;
      setEditingDevice(data);
      form.setFieldsValue({
        ...data
      });
    } catch (e) {
      // Detay alınamazsa mevcut kaydı kullan
      setEditingDevice(record);
      form.setFieldsValue({
        ...record
      });
    } finally {
      setModalVisible(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      // Onay al
      const confirmed = window.confirm('Bu cihazı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.');
      if (!confirmed) return;
      
      await axios.delete(`/api/v1/devices/${id}`);
      message.success('Cihaz silindi');
      fetchDevices();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Cihaz silinirken hata oluştu';
      message.error(errorMessage);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      if (editingDevice) {
        await axios.put(`/api/v1/devices/${editingDevice.id}`, values);
        message.success('Cihaz güncellendi');
      } else {
        await axios.post('/api/v1/devices', values);
        message.success('Cihaz eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      fetchDevices();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'İşlem sırasında hata oluştu';
      message.error(errorMessage);
    }
    finally {
      setSubmitting(false);
    }
  };


  const openControlModal = (record) => {
    setEditingDevice(record);
    controlForm.resetFields();
    setControlModalVisible(true);
  };

  const openOtaModal = (record) => {
    setEditingDevice(record);
    otaForm.resetFields();
    setSelectedFirmware(null);
    setFirmwareComparison(null);
    setOtaModalVisible(true);
  };

  const handleFirmwareSelect = (firmwareId) => {
    const firmware = firmwares.find(f => f.id === firmwareId);
    setSelectedFirmware(firmware);
    
    if (firmware && editingDevice) {
      // Firmware karşılaştırması yap
      const currentVersion = editingDevice.firmware_version;
      const newVersion = firmware.version;
      
      const comparison = {
        current: currentVersion,
        new: newVersion,
        isUpgrade: compareVersions(currentVersion, newVersion) < 0,
        isDowngrade: compareVersions(currentVersion, newVersion) > 0,
        isSame: compareVersions(currentVersion, newVersion) === 0
      };
      
      setFirmwareComparison(comparison);
    }
  };

  const compareVersions = (v1, v2) => {
    if (!v1 || !v2) return 0;
    
    const version1 = v1.replace('v', '').split('.').map(Number);
    const version2 = v2.replace('v', '').split('.').map(Number);
    
    for (let i = 0; i < Math.max(version1.length, version2.length); i++) {
      const num1 = version1[i] || 0;
      const num2 = version2[i] || 0;
      
      if (num1 < num2) return -1;
      if (num1 > num2) return 1;
    }
    return 0;
  };

  const handleSendControl = async () => {
    try {
      const values = controlForm.getFieldsValue();
      setSendingControl(true);
      // Updated to use MQTT endpoint with timeout tracking
      await axios.post(`/api/v1/mqtt/devices/${editingDevice.id}/control`, {
        command: values.command,
        params: values.params || {},
        timeout_seconds: values.timeout_seconds || 30
      });
      message.success('Komut gönderildi');
      setControlModalVisible(false);
    } catch (err) {
      message.error(err.response?.data?.detail || 'Komut gönderilemedi');
    } finally {
      setSendingControl(false);
    }
  };

  const handleSendOta = async () => {
    try {
      if (!selectedFirmware) {
        message.error('Lütfen bir firmware seçin');
        return;
      }

      setSendingOta(true);

      // VPS server-based OTA gönder
      const otaData = {
        firmware_id: selectedFirmware.id,
        version: selectedFirmware.version,
        url: selectedFirmware.file_url,
        checksum: selectedFirmware.sha256_checksum,
        file_size: selectedFirmware.file_size,
        force: otaForm.getFieldValue('force') || false
      };

      await axios.post(`/api/v1/firmware/${selectedFirmware.id}/deploy/${editingDevice.id}`, otaData);

      message.success('OTA güncellemesi başlatıldı - İlerleme cihaz listesinde görünecek');
      setOtaModalVisible(false);

      // NOT: Progress tracking artık WebSocket üzerinden real-time olarak yapılıyor
      // otaProgress state'i WebSocket listener'lar tarafından otomatik güncelleniyor
      
    } catch (err) {
      logger.error('OTA error:', err);
      message.error(err.response?.data?.detail || 'OTA güncellemesi başlatılamadı');
    } finally {
      setSendingOta(false);
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      online: { color: 'green', icon: <CheckCircleOutlined />, text: 'Çevrimiçi' },
      offline: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Çevrimdışı' },
      error: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Hata' },
      maintenance: { color: 'blue', icon: <SettingOutlined />, text: 'Bakım' },
      offline_data_pending: { color: 'yellow', icon: <WarningOutlined />, text: 'Veri Bekliyor' }
    };

    const config = statusConfig[status] || { color: 'default', icon: <InfoCircleOutlined />, text: status };

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getOperationalStatusTag = (operationalStatus, statusSummary) => {
    if (!operationalStatus) return null;

    const statusConfig = {
      idle: { color: 'default', text: 'Boşta' },
      running: { color: 'processing', text: 'Çalışıyor' },
      paused: { color: 'warning', text: 'Duraklatıldı' },
      error: { color: 'error', text: 'Hata' },
      maintenance: { color: 'warning', text: 'Bakım' }
    };

    const config = statusConfig[operationalStatus] || { color: 'default', text: operationalStatus };

    return (
      <Tooltip title={statusSummary || operationalStatus}>
        <Tag color={config.color} style={{ fontSize: '11px', marginTop: 2 }}>
          {config.text}
        </Tag>
      </Tooltip>
    );
  };

  // Filtreleme fonksiyonu
  const getFilteredDevices = () => {
    const devicesArray = Array.isArray(devices) ? devices : [];
    const searchLower = searchText.toLowerCase();
    return devicesArray.filter(device => {
      if (onlineOnly && device.status !== 'online') return false;
      if (!searchText) return true;
      return (
        device.name?.toLowerCase().includes(searchLower) ||
        device.serial_number?.toLowerCase().includes(searchLower) ||
        device.mac_address?.toLowerCase().includes(searchLower) ||
        device.model?.toLowerCase().includes(searchLower) ||
        device.firmware_version?.toLowerCase().includes(searchLower) ||
        device.stm32_firmware_version?.toLowerCase().includes(searchLower) ||
        device.customer?.name?.toLowerCase().includes(searchLower)
      );
    });
  };

  const handleSearch = (values) => {
    setSearchText(values.searchText || '');
  };

  const handleClearSearch = () => {
    setSearchText('');
    searchForm.resetFields();
  };

  const columns = [
    {
      title: 'Cihaz Adı',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text || 'İsimsiz Cihaz'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            SN: {record.serial_number}
          </Text>
        </Space>
      ),
    },
    {
      title: 'MAC Adresi',
      dataIndex: 'mac_address',
      key: 'mac_address',
      render: (text) => (
        <Text code>{text}</Text>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const progress = otaProgress[record.id];

        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {/* Cihaz durumu tag */}
            {getStatusTag(status)}

            {/* Operational status tag (telemetry'den) — sadece çevrimiçi cihazlarda göster */}
            {status === 'online' && getOperationalStatusTag(record.operational_status, record.status_summary)}

            {/* OTA Progress (sadece aktif OTA varsa) */}
            {progress && (
              <div style={{ width: '100%', marginTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span style={{ fontSize: 11, color: '#1890ff', fontWeight: 500 }}>
                    {progress.stage === 'downloading' && '📥 İndiriliyor'}
                    {progress.stage === 'verifying' && '🔐 Doğrulanıyor'}
                    {progress.stage === 'installing' && '⚙️ Yükleniyor'}
                  </span>
                  {progress.speed && (
                    <span style={{ fontSize: 10, color: '#999' }}>
                      {progress.speed.toFixed(1)} KB/s
                    </span>
                  )}
                </div>
                <Progress
                  percent={progress.percent}
                  size="small"
                  status="active"
                  strokeColor={{
                    '0%': '#1890ff',
                    '100%': '#52c41a',
                  }}
                  showInfo={true}
                  format={(percent) => `${percent}%`}
                />
                {progress.bytes && progress.total && (
                  <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                    {(progress.bytes / 1024 / 1024).toFixed(1)} MB / {(progress.total / 1024 / 1024).toFixed(1)} MB
                  </div>
                )}
              </div>
            )}
          </Space>
        );
      },
      width: 180,
      filters: [
        { text: 'Çevrimiçi', value: 'online' },
        { text: 'Çevrimdışı', value: 'offline' },
        { text: 'Hata', value: 'error' },
        { text: 'Bakım', value: 'maintenance' },
        { text: 'Veri Bekliyor', value: 'offline_data_pending' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Müşteri',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer) => customer?.name || 'Atanmamış',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (text) => text || '-',
    },
    {
      title: 'ESP32 FW',
      dataIndex: 'firmware_version',
      key: 'firmware_version',
      render: (text) => text || '-',
    },
    {
      title: 'STM32 FW',
      dataIndex: 'stm32_firmware_version',
      key: 'stm32_firmware_version',
      render: (text) => text || '—',
    },
    {
      title: 'Son Görülme',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (date) => {
        if (!date) return <span style={{ color: '#999' }}>Hiç görülmedi</span>;
        
        const relativeTime = safeGetRelativeTime(date);
        const isRecentTime = isRecent(date, 5); // 5 dakika içinde
        
        let color = '#999';
        if (isRecentTime) {
          color = '#52c41a';
        } else if (relativeTime.includes('dakika')) {
          color = '#52c41a';
        } else if (relativeTime.includes('saat')) {
          color = '#fa8c16';
        } else {
          color = '#999';
        }
        
        return (
          <Tooltip title={safeFormatTimestamp(date)}>
            <span style={{ color, fontWeight: isRecentTime ? 'bold' : 'normal' }}>
              {relativeTime}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title="Detay">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/devices/${record.id}`)}
            />
          </Tooltip>
          {hasPermission(user?.role, 'devices.edit_device') && (
            <Tooltip title="Düzenle">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {hasPermission(user?.role, 'firmware.deploy_firmware') && (
            <Tooltip title="OTA">
              <Button
                type="text"
                size="small"
                icon={<UploadOutlined />}
                onClick={() => openOtaModal(record)}
              />
            </Tooltip>
          )}
          {hasPermission(user?.role, 'devices.delete_device') && (
            <Tooltip title="Sil">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <DesktopOutlined /> Cihaz Yönetimi
        </Title>
      </div>

      {/* Device Status Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Space size="large" wrap>
            <span>
              <DesktopOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Toplam</Text>
              <Text strong style={{ fontSize: 18 }}>{stats.total}</Text>
            </span>
            <span>
              <Badge status="success" />
              <Text style={{ marginRight: 4 }}>Çevrimiçi</Text>
              <Text strong style={{ color: '#52c41a', fontSize: 18 }}>{stats.online}</Text>
            </span>
            <span>
              <Badge status="warning" />
              <Text style={{ marginRight: 4 }}>Çevrimdışı</Text>
              <Text strong style={{ color: '#fa8c16', fontSize: 18 }}>{stats.offline}</Text>
            </span>
            {stats.error > 0 && (
              <span>
                <Badge status="error" />
                <Text style={{ marginRight: 4 }}>Hata</Text>
                <Text strong style={{ color: '#f5222d', fontSize: 18 }}>{stats.error}</Text>
              </span>
            )}
            {stats.maintenance > 0 && (
              <span>
                <Badge status="processing" />
                <Text style={{ marginRight: 4 }}>Bakım</Text>
                <Text strong style={{ fontSize: 18 }}>{stats.maintenance}</Text>
              </span>
            )}
          </Space>
          <span>
            <SignalFilled style={{ color: '#52c41a', marginRight: 4 }} />
            <Text style={{ marginRight: 4 }}>Çevrimiçi</Text>
            <Text strong style={{ fontSize: 18 }}>
              {stats.total > 0 ? Math.round((stats.online / stats.total) * 100) : 0}%
            </Text>
          </span>
        </div>
      </Card>

             {/* Action Buttons and Search */}
       <Card style={{ marginBottom: 24 }}>
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
           {/* Sol taraf - Butonlar */}
           <Space>
             {hasPermission(user?.role, 'devices.add_device') && (
               <Button
                 type="primary"
                 icon={<PlusOutlined />}
                 onClick={handleAdd}
               >
                 Yeni Cihaz Ekle
               </Button>
             )}
             <Button
               icon={<ReloadOutlined />}
               onClick={fetchDevices}
               loading={loading}
             >
               Yenile
             </Button>
             <Button
               type={onlineOnly ? 'primary' : 'default'}
               icon={<CheckCircleOutlined />}
               onClick={() => setOnlineOnly(!onlineOnly)}
             >
               Sadece Çevrimiçi
             </Button>
             {hasPermission(user?.role, 'dashboard.export_data') && (
               <Button icon={<ExportOutlined />}>
                 Dışa Aktar
               </Button>
             )}
           </Space>

           {/* Sağ taraf - Arama */}
           <div style={{ minWidth: 400 }}>
             <Input.Search
               placeholder="Cihaz adı, seri no, MAC adresi, model, firmware veya müşteri ara..."
               allowClear
               value={searchText}
               onChange={(e) => setSearchText(e.target.value)}
               style={{ width: '100%' }}
             />
           </div>
         </div>
       </Card>

             {/* Devices Table */}
       <Card>
         <Table
           columns={columns}
           dataSource={getFilteredDevices()}
           rowKey="id"
           loading={loading}
           pagination={{
             showSizeChanger: !breakpoint.xs,
             showQuickJumper: !breakpoint.xs,
             showTotal: (total, range) => 
               `${range[0]}-${range[1]} / ${total} cihaz`,
             pageSizeOptions: ['10', '20', '50', '100'],
             defaultPageSize: breakpoint.xs ? 10 : 20,
             size: breakpoint.xs ? 'small' : 'default',
           }}
           scroll={{ x: breakpoint.xs ? 800 : 1200 }}
           size={breakpoint.xs ? 'small' : 'default'}
         />
       </Card>

      {/* Add/Edit Device Modal */}
      <Modal
        title={editingDevice ? 'Cihaz Düzenle' : 'Yeni Cihaz Ekle'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => setModalVisible(false)}
        width={breakpoint.xs ? '95%' : 700}
        okText={editingDevice ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{ loading: submitting }}
        style={{ top: breakpoint.xs ? 20 : undefined }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Cihaz Adı"
                rules={[
                  { required: true, message: 'Cihaz adı gerekli' },
                  { min: 2, message: 'Cihaz adı en az 2 karakter olmalı' },
                  { max: 100, message: 'Cihaz adı en fazla 100 karakter olabilir' }
                ]}
              >
                <Input placeholder="Cihaz adını girin" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="serial_number"
                label="Seri Numarası"
                rules={[
                  { min: 3, message: 'Seri numarası en az 3 karakter olmalı' },
                  { max: 50, message: 'Seri numarası en fazla 50 karakter olabilir' }
                ]}
              >
                <Input placeholder="Seri numarasını girin" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="mac_address"
                label="MAC Adresi"
                rules={[
                  { required: true, message: 'MAC adresi gerekli' },
                  { 
                    pattern: /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/, 
                    message: 'Geçerli MAC adresi girin (örn: AA-BB-CC-DD-EE-FF)' 
                  }
                ]}
              >
                <Input placeholder="AA-BB-CC-DD-EE-FF" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="model"
                label="Model"
                rules={[
                  { max: 100, message: 'Model adı en fazla 100 karakter olabilir' }
                ]}
              >
                <Input placeholder="Model bilgisini girin" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="firmware_version"
                label="Firmware Versiyonu"
                rules={[
                  { max: 50, message: 'Firmware versiyonu en fazla 50 karakter olabilir' }
                ]}
              >
                <Input placeholder="Firmware versiyonunu girin" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item name="customer_id" label="Müşteri">
                <Select placeholder="Müşteri seçin" allowClear>
                  {Array.isArray(customers) ? customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  )) : []}
                </Select>
              </Form.Item>
            </Col>
            {/* Erişim anahtarı cihaz detayına taşındı */}
          </Row>

          {/* Aktivasyon kartı cihaz detayına taşındı */}
        </Form>
      </Modal>

      {/* Control Modal */}
      <Modal
        title={`Komut Gönder: ${editingDevice?.name || ''}`}
        open={controlModalVisible}
        onCancel={() => setControlModalVisible(false)}
        onOk={handleSendControl}
        confirmLoading={sendingControl}
      >
        <Form form={controlForm} layout="vertical">
          <Form.Item label="Topic (salt okunur)">
                            <Input value={`flexer/${editingDevice?.mac_address || ''}/control`} readOnly />
          </Form.Item>
          <Form.Item name="command" label="Komut" rules={[{ required: true, message: 'Komut gerekli' }]}>
            <Input placeholder="Örn: reboot, set_param" />
          </Form.Item>
          <Form.Item name="params" label="Parametreler (JSON)">
            <Input.TextArea placeholder='{"key":"value"}' rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* OTA Modal */}
      <Modal
        title={
          <Space>
            <CloudUploadOutlined />
            <span>VPS Server Firmware OTA: {editingDevice?.name || ''}</span>
          </Space>
        }
        open={otaModalVisible}
        onCancel={() => setOtaModalVisible(false)}
        onOk={handleSendOta}
        confirmLoading={sendingOta}
        width={800}
        okText="OTA Gönder"
        cancelText="İptal"
      >
        <Alert
          message="VPS Server Firmware Güncelleme"
          description="Seçilen firmware VPS server'dan token-protected URL ile cihaza gönderilecektir."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={otaForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Cihaz Bilgileri">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <strong>Mevcut Firmware:</strong> 
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {editingDevice?.firmware_version || 'Bilinmiyor'}
                    </Tag>
                  </div>
                  <div>
                    <strong>MAC Adresi:</strong> 
                    <Text code>{editingDevice?.mac_address}</Text>
                  </div>
                  <div>
                    <strong>Durum:</strong> 
                    {getStatusTag(editingDevice?.status)}
                  </div>
                </Space>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="OTA Progress">
                {otaProgress[editingDevice?.id] !== undefined ? (
                  <Progress 
                    percent={otaProgress[editingDevice.id]} 
                    status={otaProgress[editingDevice.id] === 100 ? 'success' : 'active'}
                    strokeColor="#52c41a"
                  />
                ) : (
                  <Text type="secondary">OTA başlatılmadı</Text>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Form.Item 
            name="firmware_id" 
            label="Firmware Seçin" 
            rules={[{ required: true, message: 'Firmware seçiniz' }]}
          >
            <Select
              placeholder="VPS server firmware seçin"
              loading={firmwareLoading}
              onChange={handleFirmwareSelect}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {firmwares.map(firmware => (
                <Select.Option key={firmware.id} value={firmware.id}>
                  <Space>
                    <Tag color="blue">{firmware.version}</Tag>
                    {firmware.is_critical && (
                      <Tag color="red" size="small">Kritik</Tag>
                    )}
                    <Text type="secondary">
                      ({firmware.file_size ? `${(firmware.file_size / 1024 / 1024).toFixed(1)} MB` : 'Boyut bilinmiyor'})
                    </Text>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedFirmware && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Space direction="vertical">
                    <div>
                      <strong>Seçilen Firmware:</strong>
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        {selectedFirmware.version}
                      </Tag>
                    </div>
                    <div>
                      <strong>Kaynak:</strong>
                      <Tag color="green">VPS Server</Tag>
                    </div>
                    <div>
                      <strong>Dosya Boyutu:</strong>
                      <Text>{selectedFirmware.file_size ? `${(selectedFirmware.file_size / 1024 / 1024).toFixed(2)} MB` : 'Bilinmiyor'}</Text>
                    </div>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction="vertical">
                    <div>
                      <strong>SHA-256 Checksum:</strong>
                      <Text code style={{ fontSize: '11px' }}>
                        {selectedFirmware.sha256_checksum || 'Yok'}
                      </Text>
                    </div>
                    <div>
                      <strong>Kritik:</strong>
                      <Tag color={selectedFirmware.is_critical ? 'red' : 'green'}>
                        {selectedFirmware.is_critical ? 'Evet' : 'Hayır'}
                      </Tag>
                    </div>
                  </Space>
                </Col>
              </Row>
            </Card>
          )}

          {firmwareComparison && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text strong>Versiyon Karşılaştırması</Text>
                  <DiffOutlined />
                </div>
                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <Text type="secondary">Mevcut</Text>
                      <div>
                        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                          {firmwareComparison.current || 'Bilinmiyor'}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                  <Col span={8} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', color: '#1890ff' }}>→</div>
                    {firmwareComparison.isUpgrade && (
                      <Tag color="green" icon={<SyncOutlined />}>Yükseltme</Tag>
                    )}
                    {firmwareComparison.isDowngrade && (
                      <Tag color="orange" icon={<SyncOutlined />}>Düşürme</Tag>
                    )}
                    {firmwareComparison.isSame && (
                      <Tag color="blue">Aynı Versiyon</Tag>
                    )}
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <Text type="secondary">Hedef</Text>
                      <div>
                        <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                          {firmwareComparison.new}
                        </Tag>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Space>
            </Card>
          )}

          <Form.Item name="force" label="Zorla Güncelleme" valuePropName="checked">
            <Switch 
              checkedChildren="Zorla" 
              unCheckedChildren="Normal"
            />
            <Text type="secondary" style={{ marginLeft: 8 }}>
              Cihaz aynı versiyonda olsa bile güncelle
            </Text>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Devices;
